// DESCRIPTION: Step 5 of the event creation wizard — a read-only review/summary
// DESCRIPTION: of the whole event shown right before publishing, as two side-by-side
// DESCRIPTION: summary cards so the organizer can scan everything at once on wide screens.
import { __, sprintf } from '@wordpress/i18n';

const DAY_LABELS = {
	mon: __( 'Monday', 'nemtly-booking' ),
	tue: __( 'Tuesday', 'nemtly-booking' ),
	wed: __( 'Wednesday', 'nemtly-booking' ),
	thu: __( 'Thursday', 'nemtly-booking' ),
	fri: __( 'Friday', 'nemtly-booking' ),
	sat: __( 'Saturday', 'nemtly-booking' ),
	sun: __( 'Sunday', 'nemtly-booking' ),
};

const formatShortDate = ( dateStr ) => {
	if ( ! dateStr ) {
		return '';
	}
	const d = new Date( dateStr + 'T00:00:00' );
	if ( isNaN( d.getTime() ) ) {
		return dateStr;
	}
	return d.toLocaleDateString( undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	} );
};

const CARD =
	'nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-p-6 nb-shadow-elevation-1';
const HEADING =
	'nb-text-base nb-font-semibold nb-text-neutral-900 nb-mt-0 nb-mb-4';

/**
 * A single labeled summary row.
 * @param root0
 * @param root0.label
 * @param root0.value
 */
const Row = ( { label, value } ) => (
	<div className="nb-flex nb-justify-between nb-gap-4 nb-py-2 nb-border-b nb-border-neutral-200 last:nb-border-0">
		<dt className="nb-text-sm nb-text-neutral-500 nb-shrink-0">
			{ label }
		</dt>
		<dd className="nb-text-sm nb-text-neutral-900 nb-text-right nb-m-0">
			{ value || __( '—', 'nemtly-booking' ) }
		</dd>
	</div>
);

const ReviewStep = ( {
	formData,
	eventTypes = {},
	availableCategories = [],
	availableTags = [],
} ) => {
	const isSlotBased =
		formData.event_type === 'one-on-one' || formData.event_type === 'group';
	const isEvent = formData.event_type === 'event';

	const typeMeta = formData.event_type
		? eventTypes[ formData.event_type ]
		: null;

	const activeDays = Object.keys( DAY_LABELS ).filter( ( day ) => {
		const blocks = formData.availability?.[ day ];
		return Array.isArray( blocks ) && blocks.length > 0;
	} );

	const categoryName = ( id ) =>
		availableCategories.find( ( cat ) => cat.id === id )?.name || '';
	const tagName = ( id ) =>
		availableTags.find( ( t ) => t.id === id )?.name || '';

	const selectedCategoryNames = formData.categories
		.map( categoryName )
		.filter( Boolean );
	const selectedTagNames = formData.tags.map( tagName ).filter( Boolean );

	let scheduleValue = '';
	if ( isSlotBased ) {
		const days =
			activeDays.length > 0
				? activeDays.map( ( d ) => DAY_LABELS[ d ] ).join( ', ' )
				: __( 'No days set', 'nemtly-booking' );
		scheduleValue = sprintf(
			/* translators: 1: slot duration in minutes, 2: active days */
			__( '%1$d-min slots · %2$s', 'nemtly-booking' ),
			formData.event_duration || 60,
			days
		);
	} else if ( isEvent ) {
		const start = formatShortDate( formData.event_date );
		const end = formatShortDate( formData.event_end_date );
		const dateLine =
			start && end && start !== end ? `${ start } → ${ end }` : start;
		const timeLine = [ formData.event_time, formData.event_end_time ]
			.filter( Boolean )
			.join( ' → ' );
		scheduleValue = [ dateLine, timeLine ].filter( Boolean ).join( ' · ' );
	}

	const priceValue =
		formData.price > 0
			? sprintf( __( '$%1$s', 'nemtly-booking' ), formData.price )
			: __( 'Free', 'nemtly-booking' );

	const locationBits = [];
	if ( formData.location ) {
		locationBits.push( formData.location );
	}
	if ( formData.virtual_url ) {
		locationBits.push( formData.virtual_url.replace( /^https?:\/\//, '' ) );
	}

	const capacityValue = isSlotBased
		? formData.event_type === 'group'
			? sprintf(
					__( '%1$d per slot', 'nemtly-booking' ),
					formData.max_attendees || 0
			  )
			: __( '1 per slot', 'nemtly-booking' )
		: formData.max_attendees > 0
		? sprintf(
				__( '%1$d attendees', 'nemtly-booking' ),
				formData.max_attendees
		  )
		: __( 'Unlimited', 'nemtly-booking' );

	const cutoffValue =
		formData.booking_cutoff > 0
			? sprintf(
					__( '%1$d hrs before start', 'nemtly-booking' ),
					formData.booking_cutoff
			  )
			: __( 'Anytime', 'nemtly-booking' );

	return (
		<div className="nemtly-wizard-step-content">
			{ /* Event hero — banner, title, and type badge */ }
			<div className="nb-mb-6 nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-overflow-hidden nb-shadow-elevation-1">
				{ formData.featured_image ? (
					<img
						src={ formData.featured_image }
						alt=""
						className="nb-w-full nb-h-28 nb-object-cover"
					/>
				) : (
					<div
						className="nb-h-28 nb-bg-neutral-200"
						aria-hidden="true"
					/>
				) }
				<div className="nb-flex nb-items-start nb-justify-between nb-gap-4 nb-p-6">
					<h3 className="nb-text-2xl nb-font-bold nb-text-neutral-900 nb-m-0">
						{ formData.title ||
							__( 'Untitled event', 'nemtly-booking' ) }
					</h3>
					{ typeMeta && (
						<span className="nb-flex-none nb-inline-flex nb-items-center nb-h-8 nb-px-3.5 nb-rounded-full nb-bg-accent-500 nb-text-white nb-text-sm nb-font-semibold">
							{ typeMeta.icon ? `${ typeMeta.icon } ` : '' }
							{ typeMeta.label }
						</span>
					) }
				</div>
			</div>

			<div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-6 nb-items-start">
				<div className={ CARD }>
					<h3 className={ HEADING }>
						{ __( 'Schedule & location', 'nemtly-booking' ) }
					</h3>
					<dl className="nb-m-0">
						<Row
							label={ __( 'Schedule', 'nemtly-booking' ) }
							value={ scheduleValue }
						/>
						<Row
							label={ __( 'Timezone', 'nemtly-booking' ) }
							value={ formData.timezone }
						/>
						<Row
							label={ __( 'Location', 'nemtly-booking' ) }
							value={ locationBits.join( ' · ' ) }
						/>
						<Row
							label={ __( 'Booking closes', 'nemtly-booking' ) }
							value={ cutoffValue }
						/>
					</dl>
				</div>

				<div className={ CARD }>
					<h3 className={ HEADING }>
						{ __( 'Pricing & capacity', 'nemtly-booking' ) }
					</h3>
					<dl className="nb-m-0">
						<Row
							label={ __( 'Price', 'nemtly-booking' ) }
							value={ priceValue }
						/>
						<Row
							label={ __( 'Capacity', 'nemtly-booking' ) }
							value={ capacityValue }
						/>
						<Row
							label={ __( 'Waitlist', 'nemtly-booking' ) }
							value={
								formData.allow_waitlist
									? __( 'On', 'nemtly-booking' )
									: __( 'Off', 'nemtly-booking' )
							}
						/>
						{ selectedCategoryNames.length > 0 && (
							<Row
								label={ __( 'Categories', 'nemtly-booking' ) }
								value={ selectedCategoryNames.join( ', ' ) }
							/>
						) }
						{ selectedTagNames.length > 0 && (
							<Row
								label={ __( 'Tags', 'nemtly-booking' ) }
								value={ selectedTagNames.join( ', ' ) }
							/>
						) }
					</dl>
				</div>
			</div>
		</div>
	);
};

export default ReviewStep;
